import { Suspense, lazy, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { SnowField } from "@/components/SnowField";
import { MarketingCharacterCameos } from "@/components/MarketingCharacterCameos";
import { MarketingElfDecorations } from "@/components/MarketingElfDecorations";
import { usePrefersReducedMotion } from "@/hooks/use-prefers-reduced-motion";
import { useReveal } from "@/hooks/use-reveal";
import { useIsMobile } from "@/hooks/use-mobile";
import { useVisibilityPause } from "@/lib/useVisibilityPause";
import { TopNav } from "./TopNav";
import { Hero } from "./sections/Hero";
import { ValueStrip } from "./sections/ValueStrip";
import { HowItWorks } from "./sections/HowItWorks";
import { FeatureHighlights } from "./sections/FeatureHighlights";
import { Footer } from "./sections/Footer";
import { track } from "@/lib/analytics";
import { trackProductEvent } from "@/lib/productEvents";
import NotFound from "@/pages/not-found";

// Below-the-fold sections are code-split into separate chunks.
const AppExperience = lazy(() =>
  import("./sections/AppExperience").then((m) => ({ default: m.AppExperience })),
);
const MissionPreview = lazy(() =>
  import("./sections/MissionPreview").then((m) => ({ default: m.MissionPreview })),
);
const AudienceCards = lazy(() =>
  import("./sections/AudienceCards").then((m) => ({ default: m.AudienceCards })),
);
const ChristmasEveRescue = lazy(() =>
  import("./sections/ChristmasEveRescue").then((m) => ({ default: m.ChristmasEveRescue })),
);
const SampleClues = lazy(() =>
  import("./sections/SampleClues").then((m) => ({ default: m.SampleClues })),
);
const Comparison = lazy(() =>
  import("./sections/Comparison").then((m) => ({ default: m.Comparison })),
);
const Shareability = lazy(() =>
  import("./sections/Shareability").then((m) => ({ default: m.Shareability })),
);
const TrustSafety = lazy(() =>
  import("./sections/TrustSafety").then((m) => ({ default: m.TrustSafety })),
);
const Pricing = lazy(() =>
  import("./sections/Pricing").then((m) => ({ default: m.Pricing })),
);
// Marketing Alignment v1 — Brightbell Archive keepsake section. Static,
// no animation beyond a soft gradient. Inserted between TrustSafety and
// Pricing so the warm "why" lands right before the buy decision.
const ArchiveSection = lazy(() =>
  import("./sections/ArchiveSection").then((m) => ({
    default: m.ArchiveSection,
  })),
);
const FAQ = lazy(() =>
  import("./sections/FAQ").then((m) => ({ default: m.FAQ })),
);
// LaunchUpdatesSignup import removed: section is no longer rendered on
// the homepage (JINGLE is live; not a waitlist). The component file
// remains in src/pages/marketing/sections/ for possible future
// repurposing as a post-launch product-updates opt-in.

// Lazy-section loading placeholder, sized to its skeleton's intrinsic
// height. Pulse is gated on prefers-reduced-motion.
function LazySectionFallback() {
  const { t } = useTranslation();
  const reduced = usePrefersReducedMotion();
  const pulse = reduced ? "" : "animate-pulse";
  return (
    <div
      role="status"
      aria-label={t("marketing.loadingSection")}
      className="relative mx-auto flex w-full max-w-3xl flex-col items-center gap-3 px-5 py-10 sm:px-8 sm:py-14"
      data-testid="marketing-lazy-fallback"
    >
      <div
        aria-hidden
        className={`h-2 w-28 rounded-full bg-jingle-gold/40 ${pulse}`}
      />
      <div
        aria-hidden
        className={`mt-2 h-7 w-3/4 max-w-md rounded-md bg-jingle-snow/25 sm:h-9 ${pulse}`}
      />
      <div
        aria-hidden
        className={`h-7 w-1/2 max-w-sm rounded-md bg-jingle-snow/25 sm:h-9 ${pulse}`}
      />
      <div className="mt-4 flex w-full max-w-lg flex-col items-center gap-2">
        <div
          aria-hidden
          className={`h-2.5 w-full rounded-full bg-jingle-snow/20 ${pulse}`}
        />
        <div
          aria-hidden
          className={`h-2.5 w-11/12 rounded-full bg-jingle-snow/20 ${pulse}`}
        />
        <div
          aria-hidden
          className={`h-2.5 w-3/4 rounded-full bg-jingle-snow/20 ${pulse}`}
        />
      </div>
    </div>
  );
}

function SectionDivider() {
  return (
    <div
      aria-hidden
      className="relative mx-auto flex max-w-5xl items-center justify-center gap-4 px-5 sm:px-8"
    >
      <span className="jingle-hairline flex-1" />
      <span className="text-xs text-jingle-gold-soft/70">✦</span>
      <span className="jingle-hairline flex-1" />
    </div>
  );
}

/**
 * ShootingStars — diagonal streaking stars confined to the LEFT and
 * RIGHT vertical gutters of the marketing page. Replaces the old
 * Santa's-sleigh hero sweep with a quieter, distributed "magical
 * sky" effect that doesn't compete with the headline + phone for
 * attention. Streaks are absolutely positioned within a fixed-width
 * gutter band on each side, fall diagonally with a long bright
 * tail, and stagger their delays so the page always has at least
 * one streak in flight without ever feeling busy.
 */
function ShootingStars({ count = 7 }: { count?: number }) {
  const reduced = usePrefersReducedMotion();
  const docHidden = useVisibilityPause();
  const stars = useMemo(
    () =>
      Array.from({ length: count * 2 }, (_, i) => {
        // Even index → left gutter, odd index → right gutter.
        const side: "left" | "right" = i % 2 === 0 ? "left" : "right";
        return {
          id: i,
          side,
          // Horizontal offset within the ~10vw gutter band.
          offsetPct: 8 + Math.random() * 70,
          // Vertical start position down the page (% of total height).
          topPct: 4 + Math.random() * 88,
          // Streak length — short enough to read as a "shooting"
          // star, never a long beam.
          lengthPx: 70 + Math.random() * 80,
          // Each streak runs for 2.4–4.2s, then waits before
          // re-firing (handled by the keyframes' 0–35% active
          // window, with the rest of the cycle invisible).
          duration: 6 + Math.random() * 5,
          delay: -Math.random() * 11,
          // Tilt: left gutter streaks angle down-right (toward
          // page center), right gutter streaks angle down-left.
          rotateDeg:
            side === "left"
              ? 28 + Math.random() * 14
              : -(28 + Math.random() * 14),
        };
      }),
    [count],
  );
  if (reduced) return null;
  return (
    <div
      className="pointer-events-none absolute inset-0 z-0 overflow-hidden"
      aria-hidden
    >
      {stars.map((s) => (
        <span
          key={s.id}
          className="jingle-shooting-star absolute block"
          style={{
            // 10vw gutter band on each edge; the offsetPct positions
            // the streak's start point within that band.
            [s.side]: `calc(${s.offsetPct * 0.1}vw)`,
            top: `${s.topPct}%`,
            width: `${s.lengthPx}px`,
            height: "1.5px",
            // The rotation is applied inside the keyframe via
            // `rotate(var(--r))`; setting it as a CSS variable lets
            // each streak have its own angle without the keyframe
            // transform clobbering an inline `rotate(...)`.
            ["--r" as string]: `${s.rotateDeg}deg`,
            transformOrigin: "left center",
            animation: `jingleShootingStar ${s.duration}s linear ${s.delay}s infinite`,
            animationPlayState: docHidden ? "paused" : "running",
          }}
        />
      ))}
    </div>
  );
}

function GoldDust({ count = 22 }: { count?: number }) {
  const reduced = usePrefersReducedMotion();
  // Freeze keyframes while the tab is hidden.
  const docHidden = useVisibilityPause();
  const particles = useMemo(
    () =>
      Array.from({ length: count }, (_, i) => ({
        id: i,
        left: Math.random() * 100,
        top: 6 + Math.random() * 88,
        size: 1.2 + Math.random() * 2.4,
        delay: Math.random() * -8,
        duration: 5 + Math.random() * 6,
        opacity: 0.35 + Math.random() * 0.45,
      })),
    [count],
  );
  if (reduced) return null;
  return (
    <div
      className="pointer-events-none absolute inset-0 z-0 overflow-hidden"
      aria-hidden
    >
      {particles.map((p) => (
        <span
          key={p.id}
          className="absolute rounded-full"
          style={{
            left: `${p.left}%`,
            top: `${p.top}%`,
            width: p.size,
            height: p.size,
            opacity: p.opacity,
            background: "hsl(40, 95%, 80%)",
            boxShadow: `0 0 ${p.size * 5}px hsla(40, 95%, 70%, 0.85)`,
            animation: `jingleTwinkle ${p.duration}s ease-in-out ${p.delay}s infinite`,
            animationPlayState: docHidden ? "paused" : "running",
          }}
        />
      ))}
    </div>
  );
}

export default function MarketingPage() {
  // Native builds (Capacitor iOS / Android) ship without the marketing
  // homepage — Apple App Store §3.1.1 and the "no debug/admin/external
  // purchase UI" guidelines. `__NATIVE_BUILD__` is inlined by Vite
  // (`define` in vite.config.ts) so the early return becomes
  // `if (true) return null;` in the native bundle and rollup tree-shakes
  // the rest of this component (and its eager-imported children: TopNav,
  // Hero, ValueStrip, HowItWorks, FeatureHighlights, Footer) out.
  // Verified by `scripts/scan-native-bundle.mjs`.
  // Render the in-app NotFound (instead of returning null) so that any
  // stray native deep link / leftover nav entry produces a real 404
  // surface instead of a blank screen. The body below is still
  // unreachable code that rollup tree-shakes away — markers verified by
  // `scripts/scan-native-bundle.mjs`, route fallback verified by
  // `tools/verifyNativeRouteFallback.ts`.
  if (__NATIVE_BUILD__) return <NotFound />;
  const { t } = useTranslation();
  const ref = useReveal();
  const isMobile = useIsMobile();

  useEffect(() => {
    track("marketing_home_viewed");
    trackProductEvent("marketing_home_viewed", { source: "marketing_page" });
  }, []);
  // Lighter particle counts on mobile to protect CPU/battery and LCP.
  const snowFlakeCount = isMobile ? 35 : 90;
  const goldDustCount = isMobile ? 10 : 22;
  return (
    <div
      ref={ref}
      id="marketing-cameo-root"
      className="dark jingle-storybook-bg relative min-h-screen overflow-hidden text-jingle-snow"
      data-testid="marketing-page"
    >
      {/* Cinematic light beam haze plate behind the hero. */}
      <div
        className="jingle-light-beams pointer-events-none absolute inset-x-0 top-0 z-0 h-[110vh] opacity-90"
        aria-hidden
      />
      {/* Warm distant village glow at the bottom of the page. */}
      <div
        className="jingle-village-glow pointer-events-none absolute inset-x-0 bottom-0 z-0 h-[60vh]"
        aria-hidden
      />

      {/* Soft warm halos */}
      <div
        className="pointer-events-none absolute -right-32 -top-32 z-0 h-[520px] w-[520px] rounded-full opacity-60 blur-3xl"
        style={{
          background:
            "radial-gradient(circle at 30% 30%, hsla(38, 88%, 60%, 0.65), transparent 60%)",
        }}
        aria-hidden
      />
      <div
        className="pointer-events-none absolute left-1/2 top-[8vh] z-0 h-[380px] w-[380px] -translate-x-1/2 rounded-full opacity-55 blur-3xl"
        style={{
          background:
            "radial-gradient(circle, hsla(40, 95%, 65%, 0.45), transparent 65%)",
        }}
        aria-hidden
      />
      <div
        className="pointer-events-none absolute -left-40 top-[40vh] z-0 h-[520px] w-[520px] rounded-full opacity-40 blur-3xl"
        style={{
          background:
            "radial-gradient(circle, hsla(178, 65%, 50%, 0.32), transparent 60%)",
        }}
        aria-hidden
      />

      {/* Film-grain noise overlay */}
      <div
        className="pointer-events-none absolute inset-0 z-0 opacity-[0.06] mix-blend-overlay"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.6'/%3E%3C/svg%3E\")",
        }}
        aria-hidden
      />

      <SnowField flakeCount={snowFlakeCount} />
      <GoldDust count={goldDustCount} />
      {/* Shooting stars confined to the left + right gutter bands.
          Replaces the old Santa's-sleigh hero sweep — see the
          ShootingStars docstring for design rationale. */}
      <ShootingStars count={isMobile ? 4 : 7} />

      {/* Cinematic character cameos — Santa peeking from the hero
          left gutter, Coalie peeking from Mission Preview. (Hero
          sleigh sweep was removed; ShootingStars carries the
          magical-sky beat now.) Each clip uses mix-blend-mode:
          screen against its black plate so only the lit character
          shows through the dark pine page. */}
      <MarketingCharacterCameos />

      {/* Static elf decorations scattered down the page — lantern elf
          hanging at the value strip, gift elf at sample-clues, sitting
          elf in the pricing gutter. All decorative, pointer-events:none,
          anchored to section ids the same way the video cameos are. */}
      <MarketingElfDecorations />

      {/* Skip-to-content — invisible until keyboard-focused. Lets screen
          reader and keyboard users bypass the sticky TopNav and jump
          straight to the hero. Required for WCAG 2.4.1 ("Bypass
          Blocks") and a baseline expectation for any marketing site
          aiming for AA conformance. */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-50 focus:rounded-md focus:bg-jingle-cranberry focus:px-4 focus:py-2 focus:text-sm focus:font-semibold focus:text-jingle-snow focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-jingle-gold focus:ring-offset-2 focus:ring-offset-[hsl(354,55%,12%)]"
        data-testid="link-skip-to-content"
      >
        {t("marketing.skipToContent")}
      </a>

      <div className="relative z-10">
        <TopNav />
        <main id="main-content" tabIndex={-1}>
          <Hero />
          <ValueStrip />
          <SectionDivider />
          <HowItWorks />
          <SectionDivider />
          <Suspense
            fallback={
              <LazySectionFallback />
            }
          >
            <AppExperience />
          </Suspense>
          <SectionDivider />
          {/* Interactive Mission Preview — placed after AppExperience storytelling */}
          <Suspense
            fallback={
              <LazySectionFallback />
            }
          >
            <MissionPreview />
          </Suspense>
          <SectionDivider />
          <FeatureHighlights />
          <SectionDivider />
          {/* Audience Cards */}
          <Suspense
            fallback={
              <LazySectionFallback />
            }
          >
            <AudienceCards />
          </Suspense>
          <SectionDivider />
          {/* Christmas Eve Rescue section */}
          <Suspense
            fallback={
              <LazySectionFallback />
            }
          >
            <ChristmasEveRescue />
          </Suspense>
          <SectionDivider />
          {/* Sample Santa Clues */}
          <Suspense
            fallback={
              <LazySectionFallback />
            }
          >
            <SampleClues />
          </Suspense>
          <SectionDivider />
          {/* Comparison section */}
          <Suspense
            fallback={
              <LazySectionFallback />
            }
          >
            <Comparison />
          </Suspense>
          <SectionDivider />
          <Suspense
            fallback={
              <LazySectionFallback />
            }
          >
            <TrustSafety />
          </Suspense>
          <SectionDivider />
          {/* Marketing Alignment v1 — Brightbell Archive keepsake panel.
              Sits between TrustSafety and Pricing so the keepsake "why"
              is the last beat the visitor reads before the pricing
              decision. */}
          <Suspense
            fallback={
              <LazySectionFallback />
            }
          >
            <ArchiveSection />
          </Suspense>
          <SectionDivider />
          <Suspense
            fallback={
              <LazySectionFallback />
            }
          >
            <Pricing />
          </Suspense>
          <SectionDivider />
          <Suspense
            fallback={
              <LazySectionFallback />
            }
          >
            <FAQ />
          </Suspense>
          <SectionDivider />
          {/* Shareability section */}
          <Suspense
            fallback={
              <LazySectionFallback />
            }
          >
            <Shareability />
          </Suspense>
          {/* LaunchUpdatesSignup section intentionally not rendered:
              JINGLE is live, not a waitlist. The component file is kept
              for possible future repurposing as a real post-launch
              product-updates opt-in. The /launch-updates route still
              exists as a back-compat redirect to /create. */}
        </main>
        <Footer />
      </div>
    </div>
  );
}
